import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

const ROLES = [
  { value: "sales_associate", label: "Sales Associate" },
];

export default function Team() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", password: "", role: "sales_associate", phone: "" });

  const load = async () => setItems((await api.get("/users")).data);
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/users", f); toast.success("Sales Associate added"); setOpen(false); setF({email:"",name:"",password:"",role:"sales_associate",phone:""}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (u) => {
    await api.put(`/users/${u.id}`, { is_active: !u.is_active });
    toast.success(u.is_active ? "Deactivated" : "Activated"); load();
  };

  const associates = items.filter(u => u.role === "sales_associate");
  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-6xl mx-auto" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sales Associates</h1>
          <p className="text-sm text-slate-500">{associates.length} active associates</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-user-button"><UserPlus className="w-4 h-4 mr-1"/>Add associate</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>Add Sales Associate</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Full name</Label><Input value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} data-testid="user-name-input"/></div>
              <div><Label>Work email</Label><Input value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} data-testid="user-email-input"/></div>
              <div><Label>Temporary password</Label><Input type="password" value={f.password} onChange={(e)=>setF({...f,password:e.target.value})} data-testid="user-password-input"/></div>
              <div><Label>Phone</Label><Input value={f.phone} onChange={(e)=>setF({...f,phone:e.target.value})} data-testid="user-phone-input"/></div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-user-button">Add associate</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="p-3 text-left">Associate</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-center">Leads</th>
              <th className="p-3 text-center">Hot</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map(u => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Avatar className="w-8 h-8"><AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">{(u.name||"").slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
                    <div>
                      <div className="font-medium">{u.name}</div>
                      <div className="text-[10px] text-slate-500 capitalize">{u.role.replace("_"," ")}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-slate-600">{u.email}</td>
                <td className="p-3 text-slate-600">{u.phone || "—"}</td>
                <td className="p-3 text-center font-medium">{u.lead_count ?? "—"}</td>
                <td className="p-3 text-center">{u.hot_lead_count ? <Badge className="bg-rose-100 text-rose-800">{u.hot_lead_count}</Badge> : "—"}</td>
                <td className="p-3">{u.is_active !== false ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge className="bg-slate-100 text-slate-700">Disabled</Badge>}</td>
                <td className="p-3 text-right">
                  {u.role === "sales_associate" && (
                    <Button size="sm" variant="outline" onClick={()=>toggle(u)} data-testid={`toggle-user-${u.id}`}>
                      {u.is_active !== false ? "Deactivate" : "Activate"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
